interface UseAccountSettingsOptions {
  refreshOn?: MaybeRefOrGetter<boolean>;

  maxAgeMs?: number;
  refreshOnFocus?: boolean;
  pollMs?: number;
  immediate?: boolean;
}

export function useAccountSettings<const K extends keyof AccountSettings>(
  keysToRefresh: readonly K[],
  options: UseAccountSettingsOptions = {}
) {
  let pollTimer: ReturnType<typeof setInterval> | null = null;

  const accountSettingsStore = useAccountSettingsStore();
  const isRefreshing = ref(false);
  const lastRefreshedAt = ref<number | null>(null);

  const enabled = computed(() => {
    if (options.refreshOn === undefined) {
      return true;
    }

    return toValue(options.refreshOn);
  });

  async function refresh(maxAgeMs = options.maxAgeMs) {
    if (!enabled.value) {
      return;
    }

    isRefreshing.value = true;

    try {
      await accountSettingsStore.refreshAccountSettings(keysToRefresh, maxAgeMs);
      lastRefreshedAt.value = Date.now();
    } finally {
      isRefreshing.value = false;
    }

    return;
  }

  function stopPolling() {
    if (pollTimer !== null) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
  }

  function startPolling() {
    stopPolling();

    if (!options.pollMs || options.pollMs <= 0) {
      return;
    }

    pollTimer = setInterval(() => {
      if (!enabled.value) {
        return;
      }

      void refresh();
    }, options.pollMs);
  }

  function onWindowFocus() {
    if (!enabled.value) {
      return;
    }

    void refresh();
  }

  watch(
    enabled,
    async (isEnabled, wasEnabled) => {
      if (isEnabled && ((options.immediate ?? true) || wasEnabled === false)) {
        await refresh();
      }

      if (isEnabled) {
        startPolling();

        return;
      }

      stopPolling();
    },
    { immediate: true }
  );

  onMounted(() => {
    if (options.refreshOnFocus ?? false) {
      window.addEventListener('focus', onWindowFocus);
    }
  });

  onUnmounted(() => {
    if (options.refreshOnFocus ?? false) {
      window.removeEventListener('focus', onWindowFocus);
    }

    stopPolling();
  });

  const accountSettingsRef = computed(() => accountSettingsStore.accountSettings as Pick<AccountSettings, K>);

  const accountSettings = new Proxy({} as Pick<AccountSettings, K>, {
    get(_target, property) {
      return accountSettingsRef.value[property as keyof Pick<AccountSettings, K>];
    },
    has(_target, property) {
      return property in accountSettingsRef.value;
    },
    ownKeys() {
      return Reflect.ownKeys(accountSettingsRef.value);
    },
    getOwnPropertyDescriptor() {
      return {
        enumerable: true,
        configurable: true
      };
    }
  });

  return {
    accountSettings,
    refresh
  };
}
